import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Phone, Mail, ClipboardList, Dumbbell, Calendar, TrendingUp, Loader2,
  Video, MessageCircleQuestion, Send, CheckCircle2, Clock, Trash2, Save, Play, MessageSquare,
  RotateCcw, AlertTriangle, Activity, Scale, Ruler, Heart, Target, Wrench, Apple, Palmtree, RefreshCw, Moon, Stethoscope,
  Download, FileSpreadsheet, FileText
} from 'lucide-react';
import PhysioHistoryChart from '../../components/PhysioHistoryChart';
import ClientMonitoringPanel from '../../components/ClientMonitoringPanel';
import MacroPositionDialog from '../../components/admin/MacroPositionDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = {
  excellent: { emoji: '😀', label: 'Excelente', color: 'text-green-500' },
  good: { emoji: '😊', label: 'Bien', color: 'text-emerald-500' },
  neutral: { emoji: '😐', label: 'Normal', color: 'text-yellow-500' },
  tired: { emoji: '😓', label: 'Cansado', color: 'text-orange-500' },
  exhausted: { emoji: '😫', label: 'Agotado', color: 'text-red-500' },
};

const ClientDetailPage = () => {
  const { t } = useLanguage();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [sessionLogs, setSessionLogs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Trainer video state
  const [trainerVideo, setTrainerVideo] = useState({ video_url: '', title: '' });
  const [savingVideo, setSavingVideo] = useState(false);
  
  // Daily note state
  const [dailyNote, setDailyNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Consultations state
  const [consultations, setConsultations] = useState([]);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [respondingTo, setRespondingTo] = useState(null);
  
  // Physiological data state
  const [physioData, setPhysioData] = useState({
    age: '',
    height: '',
    weight: '',
    muscle_mass: '',
    body_fat: '',
    visceral_fat: ''
  });
  const [savingPhysio, setSavingPhysio] = useState(false);
  
  // Sessions per week state
  const [sessionsPerWeek, setSessionsPerWeek] = useState('');
  const [savingSessions, setSavingSessions] = useState(false);

  // Mesocycle tools state
  const [mesoStatus, setMesoStatus] = useState(null);
  const [loadingMesoStatus, setLoadingMesoStatus] = useState(false);
  const [preparingMeso, setPreparingMeso] = useState(false);
  // Diálogo único de reposicionar macro (reemplaza a Resetear/Ajustar/Reparar/AñadirMeso/Diagnosticar)
  const [reposDialog, setReposDialog] = useState(null);
  const [restoringMeso, setRestoringMeso] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchConsultations();
    fetchDailyNote();
  }, [clientId]);

  const fetchDailyNote = async () => {
    try {
      const response = await axios.get(`${API}/admin/daily-notes/${clientId}`);
      // Get today's note if exists
      const today = new Date().toISOString().split('T')[0];
      const todayNote = response.data?.find(n => n.date === today);
      if (todayNote) {
        setDailyNote(todayNote.content);
      }
    } catch (error) {
      console.error('Failed to fetch daily note:', error);
    }
  };

  const saveDailyNote = async () => {
    if (!dailyNote.trim()) {
      toast.error('Escribe una nota');
      return;
    }
    setSavingNote(true);
    try {
      await axios.post(`${API}/admin/daily-notes`, {
        client_id: clientId,
        content: dailyNote
      });
      toast.success('Nota guardada - El cliente la verá hoy');
    } catch (error) {
      toast.error('Error al guardar nota');
    } finally {
      setSavingNote(false);
    }
  };

  const fetchClientData = async () => {
    try {
      const [clientRes, logsRes, plansRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/session-logs/client/${clientId}`),
        axios.get(`${API}/plans`)
      ]);
      setClient(clientRes.data);
      setSessionLogs(logsRes.data);
      setPlans(plansRes.data);
      
      // Set trainer video if exists
      if (clientRes.data.trainer_video_url) {
        setTrainerVideo({
          video_url: clientRes.data.trainer_video_url,
          title: clientRes.data.trainer_video_title || 'Mensaje para el cliente'
        });
      }
      
      // Set physiological data if exists
      setPhysioData({
        age: clientRes.data.age || '',
        height: clientRes.data.height || '',
        weight: clientRes.data.weight || '',
        muscle_mass: clientRes.data.muscle_mass || '',
        body_fat: clientRes.data.body_fat || '',
        visceral_fat: clientRes.data.visceral_fat || ''
      });
      
      // Set sessions per week if exists
      setSessionsPerWeek(clientRes.data.sessions_per_week || '');
    } catch (error) {
      console.error('Failed to fetch client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API}/consultations/client/${clientId}`);
      setConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    }
  };

  const saveTrainerVideo = async () => {
    setSavingVideo(true);
    try {
      await axios.put(`${API}/clients/${clientId}/trainer-video`, {
        video_url: trainerVideo.video_url,
        title: trainerVideo.title
      });
      toast.success('Video guardado correctamente');
    } catch (error) {
      toast.error('Error al guardar video');
    } finally {
      setSavingVideo(false);
    }
  };

  const savePhysioData = async () => {
    setSavingPhysio(true);
    try {
      const dataToSend = {};
      if (physioData.age) dataToSend.age = parseInt(physioData.age);
      if (physioData.height) dataToSend.height = parseFloat(physioData.height);
      if (physioData.weight) dataToSend.weight = parseFloat(physioData.weight);
      if (physioData.muscle_mass) dataToSend.muscle_mass = parseFloat(physioData.muscle_mass);
      if (physioData.body_fat) dataToSend.body_fat = parseFloat(physioData.body_fat);
      if (physioData.visceral_fat) dataToSend.visceral_fat = parseFloat(physioData.visceral_fat);
      
      await axios.put(`${API}/clients/${clientId}`, dataToSend);
      toast.success('Datos fisiológicos guardados');
    } catch (error) {
      toast.error('Error al guardar datos');
    } finally {
      setSavingPhysio(false);
    }
  };

  const saveSessionsPerWeek = async () => {
    if (!sessionsPerWeek || sessionsPerWeek < 1 || sessionsPerWeek > 7) {
      toast.error('Introduce un número entre 1 y 7');
      return;
    }
    setSavingSessions(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        sessions_per_week: parseInt(sessionsPerWeek)
      });
      toast.success(`Configurado: ${sessionsPerWeek} sesiones/semana = ${sessionsPerWeek * 5} sesiones/mesociclo`);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSavingSessions(false);
    }
  };

  const respondToConsultation = async (consultationId, status = 'reviewed') => {
    if (!responseText.trim()) {
      toast.error('Escribe una respuesta');
      return;
    }
    
    try {
      await axios.put(`${API}/consultations/${consultationId}/respond`, {
        consultation_id: consultationId,
        response: responseText,
        status: status
      });
      toast.success('Respuesta enviada');
      setResponseText('');
      setRespondingTo(null);
      fetchConsultations();
    } catch (error) {
      toast.error('Error al responder');
    }
  };

  const deleteConsultation = async (consultationId) => {
    if (!window.confirm('¿Eliminar esta consulta?')) return;
    
    try {
      await axios.delete(`${API}/consultations/${consultationId}`);
      toast.success('Consulta eliminada');
      fetchConsultations();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs"><Clock className="h-3 w-3" /> Pendiente</span>;
      case 'reviewed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs"><MessageCircleQuestion className="h-3 w-3" /> Revisado</span>;
      case 'resolved':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs"><CheckCircle2 className="h-3 w-3" /> Resuelto</span>;
      default:
        return null;
    }
  };

  // Helper to get video embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
    const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
    return url;
  };

  const getPlanName = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.name || 'Plan desconocido';
  };

  const getMood = (moodValue) => {
    return MOODS[moodValue] || { emoji: '❓', label: 'Desconocido', color: 'text-gray-500' };
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats calculation
  const totalSessions = sessionLogs.length;
  const avgRPE = sessionLogs.length > 0 
    ? (sessionLogs.flatMap(log => log.exercises?.filter(e => e.rpe).map(e => e.rpe) || []).reduce((a, b) => a + b, 0) / 
       sessionLogs.flatMap(log => log.exercises?.filter(e => e.rpe) || []).length || 0).toFixed(1)
    : 0;
  const moodCounts = sessionLogs.reduce((acc, log) => {
    if (log.mood) acc[log.mood] = (acc[log.mood] || 0) + 1;
    return acc;
  }, {});
  const mostCommonMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0];
  const pendingConsultations = consultations.filter(c => c.status === 'pending').length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!client) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Cliente no encontrado</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/clients')}
            data-testid="back-to-clients"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>
        </div>

        {/* Client Info */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">{client.name}</h1>
                  <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {client.email}
                    </span>
                    {client.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {client.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Planes asignados</p>
                <p className="text-2xl font-bold text-primary">{client.assigned_plans?.length || 0}</p>
                <div className="flex gap-1.5 mt-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-green-600/50 text-green-400 hover:bg-green-600/20"
                    onClick={async () => {
                      try {
                        toast.info('Generando Excel...');
                        const res = await axios.get(`${API}/admin/export/client/${clientId}?format=excel`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${client.name?.replace(/\s/g, '_') || 'cliente'}.xlsx`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success('Excel descargado');
                      } catch (e) { toast.error('Error al exportar'); }
                    }}
                    data-testid="export-client-excel-btn"
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-red-600/50 text-red-400 hover:bg-red-600/20"
                    onClick={async () => {
                      try {
                        toast.info('Generando PDF...');
                        const res = await axios.get(`${API}/admin/export/client/${clientId}?format=pdf`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${client.name?.replace(/\s/g, '_') || 'cliente'}.pdf`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success('PDF descargado');
                      } catch (e) { toast.error('Error al exportar'); }
                    }}
                    data-testid="export-client-pdf-btn"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    PDF
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-primary/10">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sesiones</p>
                  <p className="text-2xl font-bold text-foreground">{totalSessions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sessions per Week - Editable */}
          <Card className="bg-gradient-to-r from-primary/10 to-amber-500/10 border-primary/30" data-testid="sessions-per-week-card">
            <CardContent className="p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Sesiones/Semana</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="7"
                    value={sessionsPerWeek}
                    onChange={(e) => setSessionsPerWeek(e.target.value)}
                    className="w-16 h-8 text-center font-bold"
                    placeholder="5"
                  />
                  <Button 
                    size="sm" 
                    onClick={saveSessionsPerWeek}
                    disabled={savingSessions}
                    className="h-8"
                  >
                    {savingSessions ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  = {(sessionsPerWeek || 0) * 5} / mesociclo
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-amber-500/10">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">RPE Promedio</p>
                  <p className="text-2xl font-bold text-foreground">{avgRPE}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-emerald-500/10">
                  <span className="text-2xl">{mostCommonMood ? getMood(mostCommonMood[0]).emoji : '❓'}</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado frecuente</p>
                  <p className="text-lg font-bold text-foreground">
                    {mostCommonMood ? getMood(mostCommonMood[0]).label : 'Sin datos'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-blue-500/10">
                  <ClipboardList className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Última sesión</p>
                  <p className="text-sm font-medium text-foreground">
                    {sessionLogs[0] ? formatDate(sessionLogs[0].completed_at).split(',')[0] : 'Nunca'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-border ${pendingConsultations > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-card'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-yellow-500/10">
                  <MessageCircleQuestion className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Consultas pendientes</p>
                  <p className={`text-2xl font-bold ${pendingConsultations > 0 ? 'text-yellow-400' : 'text-foreground'}`}>
                    {pendingConsultations}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card Nutrición */}
          <Card className={`border-border cursor-pointer transition-all hover:border-green-500/50 ${client?.modulos_comprados?.includes('nutricion') ? 'bg-green-500/10 border-green-500/30' : 'bg-card'}`}
            onClick={async () => {
              try {
                const hasAccess = client?.modulos_comprados?.includes('nutricion');
                if (hasAccess) {
                  await axios.post(`${API}/admin/nutricion/quitar-acceso/${clientId}`);
                  toast.success('Acceso a nutrición removido');
                } else {
                  await axios.post(`${API}/admin/nutricion/dar-acceso/${clientId}`);
                  toast.success('✅ Acceso a nutrición concedido');
                }
                fetchClientData();
              } catch (error) {
                toast.error('Error al cambiar acceso');
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-sm ${client?.modulos_comprados?.includes('nutricion') ? 'bg-green-500/20' : 'bg-green-500/10'}`}>
                  <Apple className={`h-5 w-5 ${client?.modulos_comprados?.includes('nutricion') ? 'text-green-400' : 'text-green-500/50'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Módulo Nutrición</p>
                  <p className={`text-sm font-bold ${client?.modulos_comprados?.includes('nutricion') ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {client?.modulos_comprados?.includes('nutricion') ? '✅ Activo' : 'Click para activar'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card Diseña tu Entreno */}
          <Card className={`border-border cursor-pointer transition-all hover:border-amber-500/50 ${client?.modulos_comprados?.includes('disena_entreno') ? 'bg-amber-500/10 border-amber-500/30' : 'bg-card'}`}
            onClick={async () => {
              try {
                const hasAccess = client?.modulos_comprados?.includes('disena_entreno');
                if (hasAccess) {
                  await axios.post(`${API}/admin/disena-entreno/quitar-acceso/${clientId}`);
                  toast.success('Acceso a Diseña tu Entreno removido');
                } else {
                  await axios.post(`${API}/admin/disena-entreno/dar-acceso/${clientId}`);
                  toast.success('✅ Acceso a Diseña tu Entreno concedido');
                }
                fetchClientData();
              } catch (error) {
                toast.error('Error al cambiar acceso');
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-sm ${client?.modulos_comprados?.includes('disena_entreno') ? 'bg-amber-500/20' : 'bg-amber-500/10'}`}>
                  <Palmtree className={`h-5 w-5 ${client?.modulos_comprados?.includes('disena_entreno') ? 'text-amber-400' : 'text-amber-500/50'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Diseña tu Entreno</p>
                  <p className={`text-sm font-bold ${client?.modulos_comprados?.includes('disena_entreno') ? 'text-amber-400' : 'text-muted-foreground'}`}>
                    {client?.modulos_comprados?.includes('disena_entreno') ? '✅ Activo' : 'Click para activar'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card Rest Mode - Experimental */}
          <Card className={`border-border cursor-pointer transition-all hover:border-purple-500/50 ${client?.experimental_features?.includes('rest_mode') ? 'bg-purple-500/10 border-purple-500/30' : 'bg-card'}`}
            onClick={async () => {
              try {
                const hasAccess = client?.experimental_features?.includes('rest_mode');
                if (hasAccess) {
                  await axios.post(`${API}/admin/client/${clientId}/rest-mode/disable`);
                  toast.success('Rest Mode desactivado');
                } else {
                  await axios.post(`${API}/admin/client/${clientId}/rest-mode/enable`);
                  toast.success('✅ Rest Mode activado');
                }
                fetchClientData();
              } catch (error) {
                toast.error('Error al cambiar Rest Mode');
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-sm ${client?.experimental_features?.includes('rest_mode') ? 'bg-purple-500/20' : 'bg-purple-500/10'}`}>
                  <Moon className={`h-5 w-5 ${client?.experimental_features?.includes('rest_mode') ? 'text-purple-400' : 'text-purple-500/50'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rest Mode</p>
                  <p className={`text-xs ${client?.experimental_features?.includes('rest_mode') ? 'text-purple-400' : 'text-muted-foreground'}`}>
                    {client?.experimental_features?.includes('rest_mode') ? '✅ Experimental' : 'Click para activar'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="monitoring" className="space-y-4">
          <TabsList className="bg-card border border-border flex-wrap">
            <TabsTrigger value="monitoring" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Monitoreo
            </TabsTrigger>
            <TabsTrigger value="note" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessageSquare className="h-4 w-4 mr-2" />
              Nota Hoy
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Calendar className="h-4 w-4 mr-2" />
              Sesiones
            </TabsTrigger>
            <TabsTrigger value="video" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Video className="h-4 w-4 mr-2" />
              Mi Video
            </TabsTrigger>
            <TabsTrigger value="consultations" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground relative">
              <MessageCircleQuestion className="h-4 w-4 mr-2" />
              Consultas
              {pendingConsultations > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 text-black text-xs font-bold rounded-full flex items-center justify-center">
                  {pendingConsultations}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="plans" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <ClipboardList className="h-4 w-4 mr-2" />
              Planes
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <TrendingUp className="h-4 w-4 mr-2" />
              Progreso
            </TabsTrigger>
            <TabsTrigger value="physio" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Activity className="h-4 w-4 mr-2" />
              Datos Físicos
            </TabsTrigger>
            <TabsTrigger value="onboarding" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground relative">
              <User className="h-4 w-4 mr-2" />
              Perfil
              {!client?.onboarding_completed && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full"></span>
              )}
            </TabsTrigger>
            <TabsTrigger value="mesocycle-tools" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Wrench className="h-4 w-4 mr-2" />
              Mesociclos
            </TabsTrigger>
          </TabsList>

          {/* MONITORING TAB - ALERTAS Y GRÁFICAS */}
          <TabsContent value="monitoring" className="space-y-4">
            <ClientMonitoringPanel clientId={clientId} />
          </TabsContent>

          {/* Daily Note Tab */}
          <TabsContent value="note" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-400" />
                  Nota del día para {client.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Esta nota aparecerá en el dashboard del cliente solo hoy. Úsala para instrucciones específicas del día.
                </p>
                <Textarea
                  value={dailyNote}
                  onChange={(e) => setDailyNote(e.target.value)}
                  placeholder="Ej: Hoy no te he puesto abdominales porque las vas a hacer así mientras..."
                  className="bg-zinc-900/50 border-zinc-800 min-h-[120px]"
                  data-testid="daily-note-input"
                />
                <Button 
                  onClick={saveDailyNote}
                  disabled={savingNote || !dailyNote.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-daily-note-button"
                >
                  {savingNote ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Guardar nota de hoy</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Session Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            {sessionLogs.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No hay registros de sesiones</p>
                </CardContent>
              </Card>
            ) : (
              sessionLogs.map((log, index) => (
                <Card key={index} className="bg-card border-border" data-testid={`session-log-${index}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{log.session_name}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {getPlanName(log.plan_id)} • Semana {log.week_number}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-3xl">{getMood(log.mood).emoji}</span>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(log.completed_at)}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {log.exercises?.map((exercise, exIndex) => (
                        <div 
                          key={exIndex} 
                          className="p-3 bg-surface rounded-sm"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Dumbbell className="h-4 w-4 text-amber-500" />
                              <span className="font-medium text-foreground">{exercise.exercise_name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              {exercise.load != null && exercise.load > 0 && (
                                <span className="text-primary font-semibold" data-testid={`exercise-avg-load-${exIndex}`}>{exercise.load} kg</span>
                              )}
                              {exercise.rpe != null && exercise.rpe > 0 && (
                                <span className={`px-2 py-1 rounded-sm ${
                                  exercise.rpe >= 9 ? 'bg-red-500/20 text-red-400' :
                                  exercise.rpe >= 7 ? 'bg-orange-500/20 text-orange-400' :
                                  exercise.rpe >= 5 ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-green-500/20 text-green-400'
                                }`}>
                                  RPE {exercise.rpe}
                                </span>
                              )}
                            </div>
                          </div>
                          {exercise.sets && exercise.sets.length > 0 && exercise.sets.some(s => s.load > 0) && (
                            <div className="flex flex-wrap gap-2 mt-2 ml-7">
                              {exercise.sets.map((s, sIdx) => (
                                <span key={sIdx} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded" data-testid={`exercise-set-${exIndex}-${sIdx}`}>
                                  S{sIdx + 1}: {s.load > 0 ? `${s.load}kg` : '-'}
                                  {s.rpe > 0 ? ` RPE${s.rpe}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {log.exercises?.some(e => e.notes) && (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Notas de ejercicios:</p>
                        {log.exercises.filter(e => e.notes).map((exercise, exIndex) => (
                          <p key={exIndex} className="text-sm text-amber-400">
                            • <span className="text-muted-foreground">{exercise.exercise_name}:</span> {exercise.notes}
                          </p>
                        ))}
                      </div>
                    )}

                    {log.general_notes && (
                      <div className="p-3 bg-primary/5 border border-primary/20 rounded-sm">
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Notas generales:</p>
                        <p className="text-sm text-foreground italic">&ldquo;{log.general_notes}&rdquo;</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Trainer Video Tab */}
          <TabsContent value="video" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5 text-primary" />
                  Video de Presentación/Seguimiento
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Sube un video personalizado para {client.name}. El cliente lo verá en su dashboard.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Título del video</Label>
                  <Input
                    value={trainerVideo.title}
                    onChange={(e) => setTrainerVideo(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ej: Bienvenida, Revisión semana 3, Correcciones..."
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL del video (YouTube, Loom, Google Drive, Vimeo)</Label>
                  <Input
                    value={trainerVideo.video_url}
                    onChange={(e) => setTrainerVideo(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="https://..."
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                
                {trainerVideo.video_url && (
                  <div className="aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe
                      src={getEmbedUrl(trainerVideo.video_url)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Preview del video"
                    />
                  </div>
                )}
                
                <Button 
                  onClick={saveTrainerVideo}
                  disabled={savingVideo}
                  className="w-full bg-primary"
                >
                  {savingVideo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar Video
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consultations Tab */}
          <TabsContent value="consultations" className="space-y-4">
            {consultations.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <MessageCircleQuestion className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No hay consultas de este cliente</p>
                </CardContent>
              </Card>
            ) : (
              consultations.map((consultation) => (
                <Card 
                  key={consultation.id} 
                  className={`border-border ${consultation.status === 'pending' ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-card'}`}
                  data-testid={`admin-consultation-${consultation.id}`}
                >
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(consultation.status)}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(consultation.created_at)}
                          </span>
                        </div>
                        <p className="text-foreground">{consultation.description}</p>
                        
                        {/* Video link */}
                        <a 
                          href={consultation.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-3 px-3 py-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                        >
                          <Play className="h-4 w-4" />
                          Ver video del cliente
                        </a>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteConsultation(consultation.id)}
                        className="text-destructive h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Existing response */}
                    {consultation.trainer_response && (
                      <div className="p-3 bg-primary/10 rounded-sm border-l-2 border-primary">
                        <p className="text-xs text-primary font-semibold mb-1">Tu respuesta:</p>
                        <p className="text-sm text-foreground">{consultation.trainer_response}</p>
                      </div>
                    )}
                    
                    {/* Response form */}
                    {respondingTo === consultation.id ? (
                      <div className="space-y-3 p-3 bg-zinc-900/50 rounded-sm">
                        <Textarea
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="Escribe tu corrección o feedback..."
                          className="bg-zinc-800 border-zinc-700 min-h-[80px]"
                        />
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => respondToConsultation(consultation.id, 'reviewed')}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Enviar (Revisado)
                          </Button>
                          <Button 
                            onClick={() => respondToConsultation(consultation.id, 'resolved')}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Enviar y Resolver
                          </Button>
                          <Button 
                            variant="ghost"
                            onClick={() => { setRespondingTo(null); setResponseText(''); }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button 
                        variant="outline"
                        onClick={() => setRespondingTo(consultation.id)}
                        className="w-full border-primary/30 text-primary hover:bg-primary/10"
                      >
                        <MessageCircleQuestion className="h-4 w-4 mr-2" />
                        {consultation.trainer_response ? 'Actualizar respuesta' : 'Responder'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans" className="space-y-4">
            {client.assigned_plans?.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No hay planes asignados</p>
                </CardContent>
              </Card>
            ) : (
              client.assigned_plans?.map((planId, index) => {
                const plan = plans.find(p => p.id === planId);
                if (!plan) return null;
                return (
                  <Card key={index} className="bg-card border-border card-hover cursor-pointer" onClick={() => navigate(`/admin/plans/${planId}`)}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground">{plan.name}</h3>
                          <p className="text-sm text-primary">{plan.objective}</p>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <p>{plan.weeks?.length || 0} semanas</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                    Progreso del Cliente
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    onClick={async () => {
                      try {
                        const response = await axios.post(`${API}/admin/client/${clientId}/recalcular-progreso`);
                        toast.success(response.data.message);
                        fetchClientData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Error al recalcular');
                      }
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Recalcular Progreso
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {client.assigned_plans?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No hay planes asignados</p>
                ) : (
                  client.assigned_plans?.map((planId) => {
                    const plan = plans.find(p => p.id === planId);
                    const progress = client.plan_progress?.[planId] || {
                      current_week: 1,
                      current_session: 1,
                      current_mesocycle: 1,
                      sessions_completed: 0
                    };
                    const totalWeeks = plan?.weeks?.length || 0;
                    const isCompleted = progress.current_week > totalWeeks;
                    
                    return (
                      <div key={planId} className="p-4 bg-muted/30 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground">{plan?.name || 'Plan'}</h3>
                          {isCompleted && (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Plan Completado
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="bg-background p-3 rounded">
                            <p className="text-muted-foreground">Semana Actual</p>
                            <p className="text-xl font-bold text-foreground">{progress.current_week} / {totalWeeks}</p>
                          </div>
                          <div className="bg-background p-3 rounded">
                            <p className="text-muted-foreground">Sesión Actual</p>
                            <p className="text-xl font-bold text-foreground">{progress.current_session}</p>
                          </div>
                          <div className="bg-background p-3 rounded">
                            <p className="text-muted-foreground">Mesociclo</p>
                            <p className="text-xl font-bold text-foreground">{progress.current_mesocycle}</p>
                          </div>
                          <div className="bg-background p-3 rounded">
                            <p className="text-muted-foreground">Sesiones Completadas</p>
                            <p className="text-xl font-bold text-foreground">{progress.sessions_completed}</p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 pt-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-400 border-amber-400/50 hover:bg-amber-400/10"
                            data-testid="reposition-client-btn"
                            onClick={() => setReposDialog({
                              planId,
                              mesocycle: progress.current_mesocycle || 1,
                              week: progress.current_week || 1,
                              session: progress.current_session || 1,
                              sessionsCompleted: progress.sessions_completed || 0,
                            })}
                          >
                            <Target className="h-4 w-4 mr-2" />
                            Reposicionar cliente
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Physiological Data Tab */}
          <TabsContent value="physio" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  Datos Fisiológicos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Edad */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      Edad
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="Ej: 45"
                        value={physioData.age}
                        onChange={(e) => setPhysioData({...physioData, age: e.target.value})}
                        className="pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">años</span>
                    </div>
                  </div>

                  {/* Altura */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Ruler className="h-4 w-4" />
                      Altura
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="Ej: 175"
                        value={physioData.height}
                        onChange={(e) => setPhysioData({...physioData, height: e.target.value})}
                        className="pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">cm</span>
                    </div>
                  </div>

                  {/* Peso */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Scale className="h-4 w-4" />
                      Peso
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Ej: 78.5"
                        value={physioData.weight}
                        onChange={(e) => setPhysioData({...physioData, weight: e.target.value})}
                        className="pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">kg</span>
                    </div>
                  </div>

                  {/* Masa Muscular */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Dumbbell className="h-4 w-4" />
                      Masa Muscular
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Ej: 35.2"
                        value={physioData.muscle_mass}
                        onChange={(e) => setPhysioData({...physioData, muscle_mass: e.target.value})}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                    </div>
                  </div>

                  {/* Grasa Corporal */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Heart className="h-4 w-4" />
                      Grasa Corporal
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Ej: 22.5"
                        value={physioData.body_fat}
                        onChange={(e) => setPhysioData({...physioData, body_fat: e.target.value})}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                    </div>
                  </div>

                  {/* Grasa Visceral */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Activity className="h-4 w-4" />
                      Grasa Visceral
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Ej: 8"
                        value={physioData.visceral_fat}
                        onChange={(e) => setPhysioData({...physioData, visceral_fat: e.target.value})}
                        className="pr-14"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">índice</span>
                    </div>
                  </div>
                </div>

                {/* Summary Card */}
                {(physioData.weight && physioData.height) && (
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">IMC Calculado</p>
                          <p className="text-2xl font-bold text-primary">
                            {(physioData.weight / Math.pow(physioData.height / 100, 2)).toFixed(1)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Clasificación</p>
                          <p className="text-lg font-semibold">
                            {(() => {
                              const imc = physioData.weight / Math.pow(physioData.height / 100, 2);
                              if (imc < 18.5) return <span className="text-blue-400">Bajo peso</span>;
                              if (imc < 25) return <span className="text-green-400">Normal</span>;
                              if (imc < 30) return <span className="text-yellow-400">Sobrepeso</span>;
                              return <span className="text-red-400">Obesidad</span>;
                            })()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Button 
                  onClick={savePhysioData} 
                  disabled={savingPhysio}
                  className="w-full md:w-auto"
                  data-testid="save-physio-data-button"
                >
                  {savingPhysio ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar Datos Fisiológicos
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Historial de Mediciones con Gráficos */}
            <PhysioHistoryChart clientId={clientId} />
          </TabsContent>

          {/* Onboarding/Perfil Tab */}
          <TabsContent value="onboarding" className="space-y-4">
            {!client?.onboarding_completed ? (
              <Card className="bg-amber-500/10 border-amber-500/30">
                <CardContent className="p-6 text-center">
                  <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Onboarding Pendiente</h3>
                  <p className="text-muted-foreground mb-4">
                    El cliente aún no ha completado su cuestionario inicial.
                  </p>
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/client/${clientId}/toggle-onboarding`);
                        toast.success('Onboarding saltado');
                        fetchClient();
                      } catch (error) {
                        toast.error('Error al saltar onboarding');
                      }
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Saltar Onboarding
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Botón para resetear onboarding - MÁS VISIBLE */}
                <Card className="bg-amber-500/10 border-amber-500/30 mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground">¿Necesitas que el cliente vuelva a completar el onboarding?</h3>
                        <p className="text-sm text-muted-foreground">El cliente verá el formulario completo de nuevo al entrar</p>
                      </div>
                      <Button 
                        variant="outline"
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/client/${clientId}/toggle-onboarding`);
                            toast.success('✅ Onboarding requerido nuevamente. El cliente lo verá al entrar.');
                            fetchClient();
                          } catch (error) {
                            toast.error('Error');
                          }
                        }}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Pedir Onboarding de Nuevo
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Objetivo */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Target className="h-5 w-5 text-primary" />
                      Objetivo del Cliente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                      <p className="text-xl font-bold text-primary">
                        {client.onboarding_data?.goal === 'perder_grasa' && '🔥 Perder grasa'}
                        {client.onboarding_data?.goal === 'ganar_musculo' && '💪 Ganar masa muscular'}
                        {client.onboarding_data?.goal === 'salud_general' && '❤️ Mejorar salud general'}
                        {client.onboarding_data?.goal === 'objetivo_guardian' && '🎯 Objetivo Guardian'}
                        {client.onboarding_data?.goal === 'atletico_funcional' && '⚡ Ser atlético funcional'}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-2xl font-bold text-foreground">{client.onboarding_data?.training_days || '-'}</p>
                        <p className="text-xs text-muted-foreground">días/semana</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-lg font-bold text-foreground capitalize">{client.onboarding_data?.experience_level || '-'}</p>
                        <p className="text-xs text-muted-foreground">nivel</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-lg font-bold text-foreground capitalize">{client.onboarding_data?.gym_access || '-'}</p>
                        <p className="text-xs text-muted-foreground">gimnasio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Datos Fisiológicos Iniciales */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Scale className="h-5 w-5 text-blue-400" />
                      Datos Fisiológicos Iniciales
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{client.onboarding_data?.weight || '-'}</p>
                        <p className="text-xs text-muted-foreground">kg</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{client.onboarding_data?.height || '-'}</p>
                        <p className="text-xs text-muted-foreground">cm</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{client.onboarding_data?.muscle_mass || '-'}</p>
                        <p className="text-xs text-muted-foreground">% músculo</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{client.onboarding_data?.body_fat || '-'}</p>
                        <p className="text-xs text-muted-foreground">% grasa</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{client.onboarding_data?.visceral_fat || '-'}</p>
                        <p className="text-xs text-muted-foreground">grasa visc.</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{client.onboarding_data?.body_water || '-'}</p>
                        <p className="text-xs text-muted-foreground">% agua</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Patologías y Limitaciones */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                      Patologías y Limitaciones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {client.onboarding_data?.pathologies?.length > 0 ? (
                          client.onboarding_data.pathologies.map((p, idx) => (
                            <span 
                              key={idx} 
                              className={`px-3 py-1 rounded-full text-sm font-medium ${
                                p === 'ninguna' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-amber-500/20 text-amber-400'
                              }`}
                            >
                              {p === 'ninguna' && '✓ Ninguna'}
                              {p === 'espalda' && '⚠️ Problemas de espalda'}
                              {p === 'rodilla' && '⚠️ Problemas de rodilla'}
                              {p === 'hombro' && '⚠️ Problemas de hombro'}
                              {p === 'hipertension' && '⚠️ Hipertensión'}
                              {p === 'diabetes' && '⚠️ Diabetes'}
                              {p === 'cardiacos' && '⚠️ Problemas cardíacos'}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted-foreground">Sin información</span>
                        )}
                      </div>
                      {client.onboarding_data?.pathology_notes && (
                        <div className="p-3 bg-zinc-900/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Notas adicionales:</p>
                          <p className="text-sm text-foreground">{client.onboarding_data.pathology_notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Fecha de registro */}
                <p className="text-xs text-muted-foreground text-center">
                  Onboarding completado: {client.onboarding_data?.completed_at ? new Date(client.onboarding_data.completed_at).toLocaleDateString('es-ES') : '-'}
                </p>
              </>
            )}
          </TabsContent>

          {/* MESOCYCLE TOOLS TAB */}
          <TabsContent value="mesocycle-tools" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-amber-400 flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Herramientas de Mesociclo
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Diagnosticar y gestionar la transición entre mesociclos
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Estado Actual */}
                <div className="p-4 bg-zinc-900/50 rounded-lg">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Estado Actual del Cliente
                  </h4>
                  
                  {mesoStatus ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Mesociclo</p>
                          <p className="text-xl font-bold text-primary">{mesoStatus.diagnosis?.current_mesocycle || '-'}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Semana</p>
                          <p className="text-xl font-bold text-foreground">{mesoStatus.diagnosis?.current_week || '-'}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Sesión</p>
                          <p className="text-xl font-bold text-foreground">{mesoStatus.diagnosis?.current_session || '-'}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Sesiones Totales</p>
                          <p className="text-xl font-bold text-green-400">{mesoStatus.diagnosis?.sessions_completed || 0}</p>
                        </div>
                      </div>
                      
                      {/* Plan Info */}
                      {mesoStatus.plan_info && (
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Plan</p>
                          <p className="font-medium text-foreground">{mesoStatus.plan_info.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {mesoStatus.plan_info.total_weeks} semanas disponibles
                          </p>
                        </div>
                      )}
                      
                      {/* Historial de Mesociclos */}
                      {mesoStatus.mesocycle_history?.length > 0 && (
                        <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                          <p className="text-xs text-green-400 mb-2">✅ Mesociclos Completados y Guardados</p>
                          <div className="space-y-1">
                            {mesoStatus.mesocycle_history.map((mh, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-foreground">Mesociclo {mh.mesocycle_number}</span>
                                <span className="text-muted-foreground">
                                  {mh.sessions_completed} sesiones · {mh.completed_at ? new Date(mh.completed_at).toLocaleDateString('es-ES') : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Session Test - Diagnóstico de carga */}
                      {mesoStatus.session_test && (
                        <div className={`p-3 rounded-lg ${mesoStatus.session_test.success ? 'bg-green-900/20 border border-green-500/30' : 'bg-red-900/20 border border-red-500/30'}`}>
                          <p className={`text-xs mb-2 ${mesoStatus.session_test.success ? 'text-green-400' : 'text-red-400'}`}>
                            {mesoStatus.session_test.success ? '✅ Test de Sesión: OK' : '❌ Test de Sesión: ERROR'}
                          </p>
                          {mesoStatus.session_test.success ? (
                            <div className="text-xs text-muted-foreground">
                              <p>Sesión: <span className="text-foreground">{mesoStatus.session_test.session_name}</span></p>
                              <p>Ejercicios: {mesoStatus.session_test.exercises_count}</p>
                            </div>
                          ) : (
                            <p className="text-xs text-red-300">{mesoStatus.session_test.error}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Últimas Sesiones */}
                      {mesoStatus.recent_sessions?.length > 0 && (
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-2">Últimas Sesiones</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {mesoStatus.recent_sessions.slice(0, 5).map((s, idx) => (
                              <div key={idx} className="flex justify-between text-xs">
                                <span className="text-foreground">{s.session_name} (Sem {s.week_number})</span>
                                <span className="text-muted-foreground">
                                  {s.completed_at ? new Date(s.completed_at).toLocaleDateString('es-ES') : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground text-sm">Click en "Ver Estado" para cargar información</p>
                    </div>
                  )}
                  
                  <Button 
                    onClick={async () => {
                      setLoadingMesoStatus(true);
                      try {
                        const response = await axios.get(`${API}/admin/clients/${clientId}/full-status`);
                        setMesoStatus(response.data);
                        toast.success('Estado cargado');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Error al cargar estado');
                      } finally {
                        setLoadingMesoStatus(false);
                      }
                    }}
                    disabled={loadingMesoStatus}
                    className="mt-4 w-full"
                    variant="outline"
                  >
                    {loadingMesoStatus ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Ver Estado Completo
                  </Button>
                </div>

                {/* Acciones */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Preparar Siguiente Mesociclo */}
                  <Card className="bg-green-900/20 border-green-500/30">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-green-400 mb-2 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Preparar Siguiente Mesociclo
                      </h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        Guarda el historial del mesociclo actual y prepara las semanas del siguiente. 
                        <strong className="text-green-400"> NO borra ningún dato.</strong>
                      </p>
                      <Button 
                        onClick={async () => {
                          if (!confirm('¿Preparar el siguiente mesociclo? El historial actual se guardará automáticamente.')) return;
                          setPreparingMeso(true);
                          try {
                            const response = await axios.post(`${API}/admin/clients/${clientId}/prepare-next-mesocycle`);
                            toast.success(response.data.message);
                            // Recargar estado
                            const statusRes = await axios.get(`${API}/admin/clients/${clientId}/full-status`);
                            setMesoStatus(statusRes.data);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Error al preparar mesociclo');
                          } finally {
                            setPreparingMeso(false);
                          }
                        }}
                        disabled={preparingMeso}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        {preparingMeso ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                        Preparar Mesociclo {(mesoStatus?.diagnosis?.current_mesocycle || 1) + 1}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Restaurar a Punto Anterior */}
                  <Card className="bg-amber-900/20 border-amber-500/30">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                        <RotateCcw className="h-4 w-4" />
                        Restaurar Progreso
                      </h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        Restaura el progreso del cliente a un punto específico.
                        <strong className="text-amber-400"> El historial NO se borra.</strong>
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={async () => {
                            if (!confirm('¿Restaurar al INICIO (Mesociclo 1, Semana 1, Sesión 1)? Esto reiniciará el progreso del cliente.')) return;
                            setRestoringMeso(true);
                            try {
                              const response = await axios.post(
                                `${API}/admin/clients/${clientId}/reset-to-mesocycle?target_mesocycle=1&target_week=1&target_session=1`
                              );
                              toast.success(response.data.message);
                              const statusRes = await axios.get(`${API}/admin/clients/${clientId}/full-status`);
                              setMesoStatus(statusRes.data);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Error al restaurar');
                            } finally {
                              setRestoringMeso(false);
                            }
                          }}
                          disabled={restoringMeso}
                          className="flex-1 bg-red-600 hover:bg-red-700"
                          variant="outline"
                        >
                          {restoringMeso ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                          Reset a Inicio
                        </Button>
                        <Button 
                          onClick={async () => {
                            if (!confirm('¿Restaurar al inicio del Mesociclo 2 (Semana 6, Sesión 1)?')) return;
                            setRestoringMeso(true);
                            try {
                              const response = await axios.post(
                                `${API}/admin/clients/${clientId}/reset-to-mesocycle?target_mesocycle=2&target_week=6&target_session=1`
                              );
                              toast.success(response.data.message);
                              const statusRes = await axios.get(`${API}/admin/clients/${clientId}/full-status`);
                              setMesoStatus(statusRes.data);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Error al restaurar');
                            } finally {
                              setRestoringMeso(false);
                            }
                          }}
                          disabled={restoringMeso}
                          className="flex-1 bg-amber-600 hover:bg-amber-700"
                          variant="outline"
                        >
                          {restoringMeso ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                          Restaurar a Meso 2
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Nota importante */}
                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Información Importante
                  </h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• El historial de mesociclos se guarda automáticamente al preparar el siguiente</li>
                    <li>• Los session_logs NUNCA se borran</li>
                    <li>• Restaurar solo cambia el punto actual, no borra historial</li>
                    <li>• Para añadir más semanas al plan, usa el PlanBuilder</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {reposDialog && (
        <MacroPositionDialog
          open={!!reposDialog}
          onClose={() => setReposDialog(null)}
          clientId={clientId}
          clientName={client?.name || 'cliente'}
          planId={reposDialog.planId}
          currentMesocycle={reposDialog.mesocycle}
          currentWeek={reposDialog.week}
          currentSession={reposDialog.session}
          sessionsCompleted={reposDialog.sessionsCompleted}
          onSuccess={fetchClientData}
        />
      )}
    </AdminLayout>
  );
};

export default ClientDetailPage;
